import CloseIcon from '@mui/icons-material/Close';
import * as Mui from "@mui/material";
import { useContext, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import FileUploader from "../../../../../../util/component/FileUploader";
import { ContextOperation } from "../../../../context/OperationContext";
import useOperationTranslation from "../../../../hooks/useOperationTranslation";
import useSnackbar from "../../../../hooks/useSnackbar";
import { AutoCompleteOption } from "../../../common/AutoComplete";
import FormInput from "../../../common/FormInput";
import { domains2AutocompleteOptions, nameof } from "../../../common/ObjectUtils";
import { ContextDamageForm } from "../../DamageFormContext";
import DamageFormService from "../../service/DamageFormService";
import { DamageFormTypes } from "../../Types";

interface Props {
    open: boolean;
    onClose: () => void;
    setSelectedAttachment?: (val: DamageFormTypes.IAttachments | undefined) => void;
    attachment?: DamageFormTypes.IAttachments;
};
const EditAttachment = (props: Props) => {
    const { tenant, setLoading } = useContext(ContextOperation);
    const { documentTypes, attachmentTypes } = useContext(ContextDamageForm);

    const t = useOperationTranslation();

    const { id } = useParams();

    const snackBar = useSnackbar();

    const form = useForm<DamageFormTypes.IAttachmentForm>({
        defaultValues: {
            attachmentTypeId: attachmentTypes?.find((type) => type.descr === props.attachment?.attachmentType)?.id,
            damageFormId: id,
            documentType: props.attachment?.documentType,
        }
    });

    const documentTypesOptions = useMemo<AutoCompleteOption[]>(() => {
        return documentTypes?.map((val) => ({ label: val.displayName, value: val.documentType })) || [];
    }, []);

    const attachmentTypesOptions = useMemo<AutoCompleteOption[]>(() => {
        return domains2AutocompleteOptions(attachmentTypes || [], "id", "descr");
    }, []);

    const file = useMemo<File>(() => {
        return new File([], props.attachment?.filename || "");
    }, [props.attachment]);

    const onFormSubmit = (data: DamageFormTypes.IAttachmentForm) => {
        if (!props.attachment || !props.attachment.id) {
            snackBar.show("warning", t("damageForm.details.generalInfo.attachments.snackbar.edit.noId"));
            return;
        }

        setLoading(true);
        DamageFormService.details.generalInfo.attachments.update(tenant, props.attachment.id.toString(), data).then(
            () => {
                onClose();
                snackBar.show("success", t("damageForm.details.generalInfo.attachments.snackbar.edit.success"));
                setLoading(false);
            },
            () => {
                snackBar.show("error", t("damageForm.details.generalInfo.attachments.snackbar.edit.error"));
                setLoading(false);
            }
        )
    }

    const onClose = () => {
        props.setSelectedAttachment && props.setSelectedAttachment(undefined);
        props.onClose();
    }

    return(
        <>   
            <Mui.Dialog 
                open={props.open} 
                onClose={onClose} 
                fullWidth
                maxWidth="sm" 
            >
                <Mui.DialogTitle>
                    <Mui.Grid container alignItems={"center"} >
                        <Mui.Grid item>
                            <Mui.Typography variant="h5">
                                {t("damageForm.details.generalInfo.attachments.dialogs.edit.title")}
                            </Mui.Typography>
                        </Mui.Grid>
                        <Mui.Grid item marginLeft={"auto"} >
                            <Mui.IconButton onClick={props.onClose}>
                                <CloseIcon />
                            </Mui.IconButton>
                        </Mui.Grid>
                    </Mui.Grid>
                </Mui.DialogTitle>
                
                <form onSubmit={form.handleSubmit(onFormSubmit)}>
                    <Mui.DialogContent>
                        <Mui.Grid container spacing={3} mb={3}>
                            <Mui.Grid item xs={12} sm={6} md={6}>
                                <FormInput
                                    name={nameof<DamageFormTypes.IAttachmentForm>("attachmentTypeId")}
                                    formInstance={form}
                                    type="select" 
                                    fullWidth  
                                    options={attachmentTypesOptions} 
                                    label={t("damageForm.details.generalInfo.attachments.dialogs.edit.lblAttachmentType")}
                                    disabled
                                />
                            </Mui.Grid>
                            <Mui.Grid item xs={12} sm={6} md={6}>
                                <FormInput
                                    name={nameof<DamageFormTypes.IAttachmentForm>("documentType")}
                                    type="select"
                                    options={documentTypesOptions}
                                    formInstance={form}
                                    fullWidth
                                    label={t("damageForm.details.generalInfo.attachments.dialogs.edit.lblDcoumentType")}
                                    disabled={form.watch("attachmentTypeId")===3?false:true}
                                /> 
                            </Mui.Grid>
                        </Mui.Grid>

                        <FileUploader
                            onUpload={() => {}}
                            uploadedFiles={[file]}
                            isDirectUpload
                        />
                    </Mui.DialogContent>

                    <Mui.DialogActions>
                        <Mui.Button variant="outlined" size="smallPlus" onClick={onClose}>
                            <Mui.Typography variant="h6" color="inherit">
                                {t("damageForm.details.generalInfo.attachments.dialogs.edit.btnCancel")}
                            </Mui.Typography>
                        </Mui.Button>
                        <Mui.Button type="submit" size="smallPlus">
                            <Mui.Typography variant="h6" color="inherit">
                                {t("damageForm.details.generalInfo.attachments.dialogs.edit.btnSave")}
                            </Mui.Typography>
                        </Mui.Button>
                    </Mui.DialogActions>
                </form>
            </Mui.Dialog>
        </>
    )
}
export default EditAttachment;